import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ShieldAlert, Search, RefreshCw, Loader2, User, Globe, Monitor } from "lucide-react";
import { toast } from "sonner";

const ROLE_STYLES = {
  driver: "bg-[#3B82F6]/10 text-[#3B82F6]",
  passenger: "bg-[#10B981]/10 text-[#10B981]",
  admin: "bg-[#FACC15]/20 text-[#0A2540]",
};

export default function AdminImpersonationLogs() {
  const [data, setData] = useState({ logs: [], total: 0, last_7d: 0, filtered_count: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/impersonation-logs", { params: { q, limit: 200 } });
      setData(payload);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(""); }, [load]);

  const submitSearch = (e) => { e.preventDefault(); load(search.trim()); };

  return (
    <div className="space-y-5" data-testid="admin-impersonation-logs-panel">
      {/* Header hero */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-5 h-5 text-[#FACC15]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Audit des sessions de support</h2>
            </div>
            <p className="text-sm text-white/70">Historique des connexions effectuées par les administrateurs sur les comptes utilisateurs (traçabilité & conformité).</p>
          </div>
          <Button onClick={() => load(search.trim())} size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="reload-impersonation-logs-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3" data-testid="impersonation-stat-total">
          <p className="text-[10px] text-[#64748B] uppercase">Total cumulé</p>
          <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{data.total}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3" data-testid="impersonation-stat-7d">
          <p className="text-[10px] text-[#64748B] uppercase">7 derniers jours</p>
          <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{data.last_7d}</p>
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3" data-testid="impersonation-stat-shown">
          <p className="text-[10px] text-[#64748B] uppercase">Affichés</p>
          <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{data.filtered_count}</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={submitSearch} className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom admin, email, cible, IP..." className="h-10 pl-9 border-[#CBD5E1]" data-testid="impersonation-search-input" />
      </form>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#0A2540] animate-spin" /></div>
      ) : data.logs.length === 0 ? (
        <div className="bg-white border border-[#CBD5E1] rounded-xl p-8 text-center" data-testid="no-impersonation-logs">
          <p className="text-sm text-[#64748B]">Aucune session enregistrée{search ? " pour cette recherche" : ""}.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#CBD5E1] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] border-b border-[#CBD5E1]">
                <tr>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Date</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Admin</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Cible</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Rôle</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">IP</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3 hidden md:table-cell">User-Agent</th>
                </tr>
              </thead>
              <tbody>
                {data.logs.map((log) => (
                  <tr key={log.id} className="border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC]" data-testid={`impersonation-log-${log.id}`}>
                    <td className="p-3 text-xs text-[#0A2540]">
                      {log.created_at ? new Date(log.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="p-3">
                      <p className="text-xs font-semibold text-[#0A2540]">{log.admin_name || "—"}</p>
                      <p className="text-[10px] text-[#64748B]">{log.admin_email || "—"}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-xs font-semibold text-[#0A2540]">{log.target_name || "—"}</p>
                      <p className="text-[10px] text-[#64748B]">{log.target_email || "—"}</p>
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ROLE_STYLES[log.target_role] || "bg-[#0A2540]/10 text-[#64748B]"}`}>{log.target_role || "—"}</span>
                      {log.mode === "assist" && (
                        <p className="text-[10px] text-[#64748B] mt-1" data-testid={`impersonation-log-assist-${log.id}`}>
                          {log.ended_at ? `Terminée ${new Date(log.ended_at).toLocaleTimeString("fr-FR", { timeStyle: "short" })}` : "Active / expirée"} · {log.bookings_created || 0} résa
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-xs font-mono text-[#0A2540]" data-testid={`impersonation-log-ip-${log.id}`}>
                      <span className="inline-flex items-center gap-1">
                        <Globe className="w-3 h-3 text-[#64748B]" /> {log.ip || "—"}
                      </span>
                    </td>
                    <td className="p-3 text-[10px] text-[#64748B] hidden md:table-cell max-w-[220px]">
                      <span className="inline-flex items-start gap-1">
                        <Monitor className="w-3 h-3 text-[#64748B] mt-0.5 shrink-0" />
                        <span className="truncate" title={log.user_agent || ""}>{log.user_agent || "—"}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
